import React from "react";
import { business, mapDirections, mapEmbed, whatsappLink } from "../data/site";
import {
  ArrowUpRight,
  Globe,
  Instagram,
  Mail,
  Phone,
  Pin,
  WhatsApp,
} from "./Icons";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import {
  btnBase,
  btnGhost,
  btnGhostSweep,
  btnIcon,
  btnLabel,
  btnSolid,
  btnSolidSweep,
  btnSweep,
  cx,
  display,
  inner,
} from "../lib/ui";

const details = [
  {
    id: "address",
    icon: Pin,
    label: "Address",
    value: business.address.lines,
    href: mapDirections,
    external: true,
    action: "Open in Maps",
  },
  {
    id: "phone",
    icon: Phone,
    label: "Phone",
    value: [business.phoneDisplay, business.phoneDisplayAlt],
    href: `tel:${business.phoneDial}`,
    action: "Call",
  },
  {
    id: "email",
    icon: Mail,
    label: "Email",
    value: [business.email],
    href: `mailto:${business.email}`,
    action: "Write",
  },
  {
    id: "website",
    icon: Globe,
    label: "Website",
    value: [business.website],
    href: business.websiteUrl,
    external: true,
    action: "Visit",
  },
  {
    id: "instagram",
    icon: Instagram,
    label: "Instagram",
    value: [business.instagramHandle],
    href: business.instagram,
    external: true,
    action: "Follow",
  },
];

export default function Contact() {
  return (
    <section id="contact" className="scroll-mt-nav bg-ink py-section text-paper">
      <div className={cx(inner, "px-gutter")}>
        <header className="grid gap-[clamp(22px,3vw,40px)] border-b border-white/15 pb-[clamp(38px,5vw,70px)] lg:grid-cols-[200px_1fr] lg:items-start lg:gap-x-[clamp(24px,4vw,60px)]">
          <SectionLabel className="!text-ash-300">Contact</SectionLabel>

          <h2 className={cx(display, "text-[clamp(38px,6.6vw,92px)] lg:col-start-2")}>
            <Reveal as="span" mask>
              Let us find
            </Reveal>
            <Reveal as="span" mask delay={90} className="italic pl-[clamp(0px,6vw,110px)]">
              your address.
            </Reveal>
          </h2>

          <Reveal className="flex flex-wrap gap-3 lg:col-start-2" delay={180}>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer noopener"
              className={cx(btnBase, btnSolid)}
            >
              <span className={cx(btnSweep, btnSolidSweep)} />
              <WhatsApp width="16" height="16" className="relative z-10" />
              <span className={btnLabel}>WhatsApp us</span>
              <ArrowUpRight width="15" height="15" className={btnIcon} />
            </a>
            <a href={`tel:${business.phoneDial}`} className={cx(btnBase, btnGhost)}>
              <span className={cx(btnSweep, btnGhostSweep)} />
              <Phone width="15" height="15" className="relative z-10" />
              <span className={btnLabel}>{business.phoneDisplay}</span>
            </a>
          </Reveal>
        </header>

        <div className="grid items-start gap-[clamp(34px,4vw,70px)] pt-[clamp(34px,4.5vw,66px)] lg:grid-cols-[1fr_1.05fr]">
          {/* ---------- detail rows + photograph ---------- */}
          <div className="grid gap-[clamp(28px,4vw,52px)]">
            <ul className="border-t border-white/15">
              {details.map((item, i) => {
                const Icon = item.icon;
                return (
                  <Reveal as="li" key={item.id} delay={i * 70} className="border-b border-white/15">
                    <a
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer noopener" : undefined}
                      className="group grid grid-cols-[34px_1fr_auto] items-center gap-3.5 py-[18px] transition-[padding] duration-500 ease-out hover:pl-3"
                    >
                      <span className="grid h-[34px] w-[34px] place-items-center rounded-full border border-white/15 text-ash-200 transition-colors duration-[400ms] ease-out group-hover:border-paper group-hover:bg-paper group-hover:text-ink">
                        <Icon width="17" height="17" />
                      </span>

                      <span className="grid min-w-0 gap-0.5">
                        <em className="text-[10px] uppercase not-italic tracking-[0.24em] text-ash-400">
                          {item.label}
                        </em>
                        {item.value.map((line) => (
                          <span
                            key={line}
                            className="break-words text-[clamp(14px,1.25vw,16px)] text-paper"
                          >
                            {line}
                          </span>
                        ))}
                      </span>

                      <span className="hidden items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-ash-400 transition-[color,gap] duration-[350ms] ease-out group-hover:gap-2.5 group-hover:text-paper sm:inline-flex">
                        {item.action}
                        <ArrowUpRight width="13" height="13" />
                      </span>
                    </a>
                  </Reveal>
                );
              })}
            </ul>

            <Reveal className="group relative overflow-hidden" delay={200}>
              <img
                src="/images/team-collab.jpg"
                alt="Hands meeting in the middle — working together on a deal"
                loading="lazy"
                className="h-[clamp(220px,30vw,340px)] w-full scale-[1.03] object-cover grayscale contrast-[1.1] brightness-[0.8] transition-[transform,filter] duration-[1200ms] ease-out group-hover:scale-[1.08] group-hover:brightness-95"
              />
              <figcaption className="absolute inset-x-0 bottom-0 grid gap-1 bg-caption-scrim p-5">
                <strong className="text-xs font-medium uppercase tracking-[0.16em]">
                  {business.hours}
                </strong>
                <span className="text-[13px] text-ash-200">
                  Walk in, or send us the brief first — either works.
                </span>
              </figcaption>
            </Reveal>
          </div>

          {/* ---------- map ---------- */}
          <Reveal className="grid gap-4 lg:sticky lg:top-[calc(theme(spacing.nav)+28px)]" delay={120}>
            <div className="group relative aspect-[4/3] overflow-hidden border border-white/15 bg-ink-800">
              <iframe
                title={`Map to ${business.name}`}
                src={mapEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                /* Google ships colour tiles; invert + desaturate gives a dark
                   monochrome map that matches the rest of the page. */
                className="absolute inset-0 h-full w-full border-0 grayscale invert contrast-[0.86] brightness-[0.94] transition-[filter] duration-[600ms] group-hover:grayscale-[0.25] group-hover:brightness-100"
              />
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3.5">
              <p className="grid max-w-[44ch] gap-1">
                <strong className="font-display text-[19px] font-normal">{business.name}</strong>
                <span className="text-[12.5px] leading-[1.55] text-ash-300">
                  {business.address.oneLine}
                </span>
              </p>
              <a
                href={mapDirections}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex flex-none items-center gap-[7px] border-b border-paper pb-[3px] text-[11px] font-medium uppercase tracking-[0.18em] transition-[gap,opacity] duration-[350ms] ease-out hover:gap-3 hover:opacity-70"
              >
                Get directions
                <ArrowUpRight width="14" height="14" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
